import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getEventsAndNotifications,
  markAsRead,
} from "../../services/EventsNotifications";
import { getAllDeviceList } from "../../services/DeviceService";
import Pagination from "../../utils/components/common/pagination";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import DropDown from "../../utils/components/common/dropdown/dropdown";
import {
  UserContext,
  OrganisationContext,
  NotificationContext,
} from "../../layouts/MainLayout";
import { timeStringToTimeAMPM } from "../../utils/utils";
import Icons from "../../utils/components/icons";
import SearchDevice from "../../utils/components/common/searchDevice/SearchDevice";
import Modal from "../../utils/components/common/modal";

import "./EventsNotification.scss";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";

const filtersObj = {
  All: "All",
  cartridge: "Cartridge",
  connectivity: "Connectivity",
  clo2Value: "PPB Level",
  faults: "Fault",
  deviceEvents: "Device Events",
};

const tableHeadings = [
  "isNotification",
  "deviceName",
  "eventType",
  "eventName",
  "eventMsg",
  "timestamp",
  "readUnread",
];

const ItemPerPage = 10;

const EventsNotification = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("eventsNotification");
  const { deviceId } = useParams();

  const { userCognitoData, appTheme } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);
  const { setIsEvtListedFromDevice } = useContext(NotificationContext);

  const durationOption = [
    { label: t("duration.last24hrs"), value: 24 },
    { label: t("duration.last48hrs"), value: 48 },
    { label: t("duration.last72hrs"), value: 72 },
    { label: t("duration.last96hrs"), value: 96 },
    { label: t("duration.last120hrs"), value: 120 },
  ];

  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedDeviceId, setSelectedDeviceId] = useState(deviceId);
  const [allDevices, setAllDevices] = useState();
  const [pageTotalCount, setPageTotalCount] = useState();
  const [evtsNotificationData, setEvtsNotificationData] = useState();
  const [evtsNotificationCounts, setEvtsNotificationCounts] = useState();
  const [pageCount, setPageCount] = useState(1);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [durationVal, setDurationVal] = useState(24);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const deviceName = useMemo(() => {
    if (allDevices && deviceId) {
      const data = allDevices.find((device) => device.DeviceUUID === deviceId);
      return data?.DeviceName;
    }
    return "";
  }, [allDevices, deviceId]);

  /**Handled get All Device Data for dropdown [On site change]**/
  useEffect(() => {
    const tenantId = siteData.id;
    setIsLoading(true);
    if (!tenantId) return;
    setAllDevices();
    const queryParams = {
      tenantId: tenantId,
      sort: [{ DeviceName: "asc" }],
    };
    getAllDeviceList(queryParams)
      .then((response) => {
        setAllDevices(response.data.Items);
      })
      .catch((err) => {
        setAllDevices();
        setIsLoading(false);
      });
  }, [siteData]);

  const markAllAsListed = useCallback(() => {
    if (userCognitoData.username && siteData.id) {
      // setListed(true);
      const param = {
        userId: userCognitoData.username,
        siteId: siteData.id,
        deviceId: deviceId,
        eventType: "All",
        duration: 24,
        isListed: true,
      };
      markAsRead(param)
        .then((res) => {
          setIsEvtListedFromDevice((prev) => !prev);
        })
        .catch((err) => {
          //   console.log(err);
        });
    }
  }, [
    siteData.id,
    userCognitoData.username,
    setIsEvtListedFromDevice,
    deviceId,
  ]);

  useEffect(() => {
    if (deviceId) {
      markAllAsListed();
    }
  }, [markAllAsListed, deviceId]);

  /**Get events & Notifications Method **/
  const fetchEventAndNotifications = useCallback((param) => {
    getEventsAndNotifications(param)
      .then((response) => {
        setEvtsNotificationCounts(response?.data?.CountsAssociatedWithFilter);
        const data = response?.data?.Items || [];
        setIsLoading(false);
        // setIsEvtListedFromDevice((prev) => !prev);
        // console.log(data)
        setEvtsNotificationData(data);
        setPageTotalCount(
          Math.ceil(response.data.TotalEventsCount / ItemPerPage)
        );
      })
      .catch((err) => {
        setIsLoading(false);
        setEvtsNotificationData([]);
        setShowErrorPopup(true);
      });
  }, []);

  /**Handled get events & Notifications **/
  useEffect(() => {
    setIsLoading(true);
    if (!(userCognitoData?.username && siteData?.id && allDevices)) return;
    setEvtsNotificationData();

    /**To update the context value to trigger the header notification count reset */

    const param = {
      userId: userCognitoData.username,
      siteId: siteData.id,
      eventType: activeFilter,
      duration: durationVal,
      size: ItemPerPage,
      pageNo: pageCount,
      showEvent: true,
      timeStamp: "DESC",
    };
    if (selectedDeviceId) {
      /**To handle site change condition */
      if (allDevices.find((device) => device.DeviceUUID === selectedDeviceId)) {
        param.deviceId = selectedDeviceId;
      }
    }
    if (showOnlyUnread) {
      param.showUnread = showOnlyUnread;
    }
    fetchEventAndNotifications(param);
  }, [
    userCognitoData,
    pageCount,
    activeFilter,
    showOnlyUnread,
    durationVal,
    siteData,
    selectedDeviceId,
    allDevices,
    fetchEventAndNotifications,
  ]);

  const markRead = useCallback(
    (id, isRead) => {
      if (isRead) return;
      const param = {
        userId: userCognitoData.username,
        siteId: siteData.id,
        isRead: !isRead,
        isListed: true,
        notificationId: [id],
      };
      markAsRead(param)
        .then((res) => {
          const updatedData = evtsNotificationData.map((data) => {
            if (data.PK === id) {
              return { ...data, IsRead: !data.IsRead };
            }
            return data;
          });
          setEvtsNotificationData(updatedData);
        })
        .catch((err) => {
          // console.log(err);
        });
    },
    [evtsNotificationData, userCognitoData.username, siteData]
  );

  const handlePageChange = useCallback((item) => {
    setPageCount(item + 1);
  }, []);

  /**Go back to device view**/
  const goBack = useCallback(() => {
    navigate(`/device-view/${deviceId}`);
  }, [deviceId, navigate]);

  /**Go to device view on device click **/
  const openDeviceView = useCallback(
    (deviceId) => {
      navigate(`/device-view/${deviceId}`);
    },
    [navigate]
  );

  const updateDuration = useCallback((val) => {
    setDurationVal(val);
    setPageCount(1); //Reset pagecount to 1
  }, []);

  const onDeviceSelectionChange = useCallback((id) => {
    setSelectedDeviceId(id);
    setPageCount(1); //Reset pagecount to 1
  }, []);

  const showOnlyUnreadEvent = useCallback(() => {
    setShowOnlyUnread(!showOnlyUnread);
    setPageCount(1); //Reset pagecount to 1
  }, [showOnlyUnread]);

  return (
    <div className="bodyInner events-notifications-page">
      <div className="bodyContainer">
        {/* Header Section Starts */}
        <div className="controllesWrapper fixSubHeader">
          <div className="page-heading">
            {deviceId ? (
              <>
                {" "}
                <span
                  className="back-to-device pointer heading-1"
                  onClick={goBack}
                >
                  {t("device_evt_notification_heading_1")}
                </span>
                <span className="heading-2">
                  {t("device_evt_notification_heading_2")}
                </span>
              </>
            ) : (
              <>{t("event_notification_page_heading")}</>
            )}
          </div>
          <div className="right-container">
            {deviceId ? (
              <label className={deviceName ? "selected-device disabled" : ""}>
                {deviceName}
              </label>
            ) : (
              <SearchDevice
                allDevices={allDevices || []}
                showNoSelection={true}
                // resetSelection={() => setSelectedDeviceId()}
                onClose={(id) => {
                  onDeviceSelectionChange(id);
                }}
              />
            )}
            <DropDown
              options={durationOption}
              wrapperClass="headerDropdown"
              autoid="duration"
              value={durationVal}
              onSelection={updateDuration}
              startIcon={Icons.clockIcon}
            />
          </div>
        </div>
        {/* Header Section Ends */}
        {/* Filter Section Starts */}
        <div className="filters fixSubHeader">
          <div>
            {Object.keys(filtersObj).map((filter) => {
              return (
                <label
                  className={
                    filter === activeFilter
                      ? "btn btn-primary active"
                      : "btn btn-primary"
                  }
                  onClick={() => {
                    setActiveFilter(filter);
                    setPageCount(1);
                  }}
                  key={filter}
                  id={filter}
                >
                  <input type="radio" name="options" id="" className="hide" />
                  {`${filtersObj[filter]} (${
                    evtsNotificationCounts
                      ? evtsNotificationCounts["Count_EventType_" + filter]
                      : 0
                  })`}
                </label>
              );
            })}
          </div>
          <div className="form-check form-switch" id="onlyShowUnread">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckChecked"
              checked={showOnlyUnread}
              onChange={showOnlyUnreadEvent}
            />
            <label>{t("only_show_unread")}</label>
          </div>
        </div>
        {/* Filter Section Ends */}

        {/* Table Section Starts */}
        <div className="gridViewWrapper">
          <div className="gridViewInner">
            {!isLoading && evtsNotificationData?.length ? (
              <table
                className="table table-striped evt-notification theme-table"
                id="event_notification_table"
              >
                <thead>
                  <tr>
                    {tableHeadings.map((heading) => {
                      return (
                        <th
                          key={heading}
                          className={heading === "eventMsg" ? "wid30" : ""}
                        >
                          {t(`eventsPageTableHeadings.${heading}`)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {evtsNotificationData?.map((data, i) => {
                    return (
                      <tr
                        key={`${data?.EventUUID + i}`}
                        className={data.IsRead ? "read" : "unread"}
                      >
                        <td>
                          {data.IsNotification && (
                            <>
                              {data?.IsRead ? (
                                <span>
                                  <img
                                    src={
                                      appTheme === "light"
                                        ? require("../../assets/images/icons/disabled_notification.png")
                                        : require("../../assets/images/icons/dark_disabled_notification.png")
                                    }
                                    className="notify"
                                    alt="notification"
                                  />
                                </span>
                              ) : (
                                <img
                                  src={
                                    appTheme === "light"
                                      ? require("../../assets/images/icons/notification.png")
                                      : require("../../assets/images/icons/dark_notification.png")
                                  }
                                  className="notify"
                                  alt="notification"
                                />
                              )}
                            </>
                          )}
                        </td>
                        <td
                          className="pointer"
                          onClick={() => {
                            openDeviceView(data.DeviceUUID);
                          }}
                          title={t("hover.openDeviceView")}
                        >
                          {data?.DeviceName}
                        </td>
                        <td>{filtersObj[data.EventType]}</td>
                        <td>{data?.EventName}</td>
                        <td>{data?.NotificationBody}</td>
                        <td>
                          {timeStringToTimeAMPM(data?.EventEstimatedTimeStamp)}
                        </td>
                        <td
                          className={
                            !data.IsRead ? "mail-icon pointer" : "mail-icon"
                          }
                          title={!data.IsRead ? t("markAsRead") : ""}
                          onClick={() => {
                            markRead(data.PK, data?.IsRead);
                          }}
                        >
                          {data?.IsRead
                            ? Icons.readMailIcon
                            : Icons.unreadMailIcon}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <></>
            )}
            {/* Pagination Section Starts */}

            {/* Pagination Section Ends */}
          </div>
          {!isLoading &&
            !evtsNotificationData?.length &&
            activeFilter === "All" && (
              <EmptyScreen
                img={Icons.noEventIcon}
                title={t("Empty_screen_all_title")}
                desc={t("Empty_screen_all_desc")}
              />
            )}
          {!isLoading &&
            !evtsNotificationData?.length &&
            activeFilter !== "All" && (
              <EmptyScreen
                img={Icons.noEventIcon}
                title={t("Empty_screen_title")}
                desc={t("Empty_screen_desc")}
              />
            )}
        </div>

        <div className="bodyFooter footerFixed">
          <Pagination
            currentPage={pageCount - 1}
            totalPages={pageTotalCount}
            pageCallBack={handlePageChange}
          />
        </div>
        {/* Table Section Ends */}
        {isLoading && <LoadingSpinner />}
      </div>
      {/* Error Modal/Popup */}
      {showErrorPopup ? (
        <ModalComponent
          idName="eventsNotifications_error_modal"
          isOpen={showErrorPopup}
          onCloseModal={() => setShowErrorPopup(false)}
          descriptionText={t("filterErrorMsg")}
        ></ModalComponent>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EventsNotification;
